'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdAdd, MdWaterDrop, MdSearch, MdClose } from 'react-icons/md'
import { getFluidBalances, FluidBalance } from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function FluidBalancePage() {
    const navigate = useNavigate()
    const [balances, setBalances] = useState<FluidBalance[]>([])
    const [filteredBalances, setFilteredBalances] = useState<FluidBalance[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const {
        hospitals,
        loading: loadingHospitals,
        selectedHospitalId,
        setSelected,
    } = useHospitals()

    useEffect(() => {
        loadBalances()
    }, [selectedHospitalId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = balances.filter(b =>
                b.admission?.client.name
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
            )
            setFilteredBalances(filtered)
        } else {
            setFilteredBalances(balances)
        }
    }, [searchTerm, balances])

    const loadBalances = async () => {
        setLoading(true)
        try {
            const response = await getFluidBalances(undefined, {
                hospitalId: selectedHospitalId || undefined,
            })
            if (response.success) {
                setBalances(response.result)
                setFilteredBalances(response.result)
            } else {
                toast.error(
                    response.error || 'Erro ao carregar balanços hídricos'
                )
            }
        } catch (error) {
            toast.error('Erro ao carregar balanços hídricos')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando balanços hídricos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center shadow-lg">
                        <MdWaterDrop className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Balanço Hídrico
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Controle de ingestão e eliminação de líquidos
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => navigate('/hospital/fluid-balance/new')}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Novo</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por paciente..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredBalances.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdWaterDrop className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhum balanço encontrado
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Comece criando um balanço hídrico
                        </p>
                        <button
                            onClick={() =>
                                navigate('/hospital/fluid-balance/new')
                            }
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-xl" />
                            Novo balanço
                        </button>
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Paciente</th>
                                <th>Leito</th>
                                <th>Data</th>
                                <th>Entradas</th>
                                <th>Saídas</th>
                                <th>Balanço</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBalances.map(balance => (
                                <tr
                                    key={balance.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(
                                            `/hospital/fluid-balance/${balance.id}`
                                        )
                                    }
                                >
                                    <td>
                                        <div className="font-bold">
                                            {balance.admission?.client.name}
                                        </div>
                                    </td>
                                    <td>
                                        {balance.admission?.bed?.number || '-'}
                                    </td>
                                    <td>
                                        {new Date(
                                            balance.date
                                        ).toLocaleDateString('pt-BR')}
                                    </td>
                                    <td className="text-success">
                                        +{balance.totalInput}ml
                                    </td>
                                    <td className="text-error">
                                        -{balance.totalOutput}ml
                                    </td>
                                    <td
                                        className={
                                            balance.balance >= 0
                                                ? 'text-success font-bold'
                                                : 'text-error font-bold'
                                        }
                                    >
                                        {balance.balance >= 0 ? '+' : ''}
                                        {balance.balance}ml
                                    </td>
                                    <td>
                                        <span
                                            className={`badge ${balance.closedAt ? 'badge-ghost' : 'badge-success'}`}
                                        >
                                            {balance.closedAt
                                                ? 'Fechado'
                                                : 'Aberto'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
